<script setup>
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth.js'
import { storeToRefs } from 'pinia'
import { calcPriceToShow } from '@/helpers/tax-helpers.js'

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  selectedProductId: {
    type: [String, Number],
    default: null
  },
  handleSubmit: {
    type: Function,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  currency: {
    type: String,
    default: 'USD'
  }
})

const authStore = useAuthStore()

const {
  options
} = storeToRefs(authStore)

const priceWithTaxDisplayMode = computed(() => {
  return options.value.taxesByCountryFs?.priceWithTaxDisplayMode
})

const priceToShow = computed(() => calcPriceToShow(props.item.priceInCurrency, false))
const pricePerCreditToShow = computed(() => calcPriceToShow(props.item.priceInCurrencyPerCredit, false))
</script>

<template>
  <div
    class="h-[8rem] sm:h-34 w-[100px] sm:w-[115px] m-1 relative rounded-lg flex flex-col justify-between cursor-pointer"
    :class="[
      item.id === selectedProductId ? 'shadow-as-border' : 'shadow-as-border-gray',
    ]">
    <header class="w-full h-4/6 rounded-t-lg flex bg-white flex-col items-center justify-center relative">
      <div
        v-if="item.bonusCreditCount"
        class="bg-primary text-xs w-max mx-auto text-white rounded-full px-2 py-0.5 absolute -top-2.5 left-0 right-0">
        +{{ item.bonusCreditCount }} {{ $t('common.credits') }}
      </div>

      <div class="text-center relative pt-2">
        <h4 class="text-lg font-bold leading-4 flex items-center">
          {{ item.creditCount }}

          <InlineSvg
            class="h-4 w-4 ml-1"
            :src="require('@/assets/svg/design/coin.svg')"/>
        </h4>

        <small class="text-caption text-gray-900">{{ $t('user.credits.credits', item.creditCount) }}</small>

        <div
          v-show="loading && item.id === selectedProductId"
          class="absolute left-0 right-0 -bottom-6 sk-pulse sk-center"/>
      </div>

      <div
        v-if="item.isTop"
        class="text-primary font-bold text-sm pt-1.5">
        TOP
      </div>

      <div
        v-else-if="item.discountInPercents"
        class="text-primary font-bold text-sm pt-1.5">
        SAVE {{ item.discountInPercents }}%
      </div>
    </header>

    <footer
      :class="[item.id === selectedProductId ? 'tariff-gradient' : 'bg-gray-100']"
      class="w-full h-2/6 rounded-b-lg flex flex-col items-center justify-center">
      <h5
        :class="[item.id === selectedProductId ? 'text-white' : 'text-black']"
        class="text-sm font-semibold leading-4 pt-1 -mr-1">
        {{ priceToShow }} {{ currency }}<span :class="{ 'invisible': !(priceWithTaxDisplayMode && item.id === selectedProductId) }">*</span>
      </h5>

      <div
        :class="[item.id === selectedProductId ? 'text-white' : 'text-gray-900']"
        class="text-xs whitespace-nowrap">
        ${{ pricePerCreditToShow }}/{{ $t('user.credits.credits') }}
      </div>
    </footer>
  </div>
</template>

<style scoped>
  .tariff-gradient {
    background: linear-gradient(180deg, #F23459 0%, #C01855 100%), #F23459;
  }

  .shadow-as-border {
    box-shadow: 0 0 0 2px #f23459;
  }

  .shadow-as-border-gray {
    box-shadow: 0 0 0 2px #9ca3af4a;
  }
</style>